import { useState, useRef, useEffect } from "react";
import { Link } from "@remix-run/react";
import type { MetaFunction } from "@remix-run/node";
import { motion, useMotionValue, PanInfo } from 'framer-motion';
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/features/greeting-cards/greeting-cards.css";

import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('greetingCards');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('greetingCards');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "بطاقات التهنئة - أسامة الزبيدي | أنشئ بطاقات تهنئة مخصصة مجانية | Greeting Cards - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "بطاقات التهنئة - أسامة الزبيدي | أنشئ بطاقات تهنئة مخصصة مجانية" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/greeting-cards" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "بطاقات التهنئة - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "بطاقات التهنئة - أسامة الزبيدي | أنشئ بطاقات تهنئة مخصصة مجانية" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "بطاقات التهنئة - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/greeting-cards" },
    { name: "alternate", content: "https://oz.sa/greeting-cards", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/greeting-cards", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/greeting-cards", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

// Fixed card dimensions for 9:16 aspect ratio
const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1920;

// Card type categories - Optimized order and enhanced properties
const cardTypes = [
  // Religious & Cultural Events (Highest Priority)
  {
    id: 'eid-al-fitr',
    name: { ar: 'عيد الفطر', en: 'Eid al-Fitr' },
    description: { ar: 'بطاقات تهنئة بعيد الفطر المبارك', en: 'Eid al-Fitr greeting cards' },
    icon: '🌙',
    color: 'from-green-400 to-emerald-600',
    priority: 1,
    category: 'religious',
    isPopular: true,
    isSeasonal: true
  },
  {
    id: 'eid-al-adha',
    name: { ar: 'عيد الأضحى', en: 'Eid al-Adha' },
    description: { ar: 'بطاقات تهنئة بعيد الأضحى المبارك', en: 'Eid al-Adha greeting cards' },
    icon: '🕌',
    color: 'from-red-400 to-orange-600',
    priority: 2,
    category: 'religious',
    isPopular: true,
    isSeasonal: true
  },
  {
    id: 'ramadan',
    name: { ar: 'رمضان', en: 'Ramadan' },
    description: { ar: 'بطاقات تهنئة بشهر رمضان المبارك', en: 'Ramadan greeting cards' },
    icon: '☪️',
    color: 'from-purple-400 to-purple-600',
    priority: 3,
    category: 'religious',
    isPopular: true,
    isSeasonal: true
  },
  
  // Personal Celebrations (High Priority)
  {
    id: 'birthday',
    name: { ar: 'عيد الميلاد', en: 'Birthday' },
    description: { ar: 'بطاقات تهنئة بعيد الميلاد', en: 'Birthday cards' },
    icon: '🎂',
    color: 'from-yellow-400 to-orange-500',
    priority: 4,
    category: 'personal',
    isPopular: true,
    isSeasonal: false
  },
  {
    id: 'marriage',
    name: { ar: 'الزواج', en: 'Marriage' },
    description: { ar: 'بطاقات تهنئة بالزواج', en: 'Marriage cards' },
    icon: '💒',
    color: 'from-red-500 to-pink-600',
    priority: 5,
    category: 'personal',
    isPopular: true,
    isSeasonal: false
  },
  {
    id: 'new-born',
    name: { ar: 'المولود الجديد', en: 'New Born Baby' },
    description: { ar: 'بطاقات تهنئة بالمولود الجديد', en: 'New born baby cards' },
    icon: '👶',
    color: 'from-pink-400 to-rose-600',
    priority: 6,
    category: 'personal',
    isPopular: true,
    isSeasonal: false
  },
  {
    id: 'graduate',
    name: { ar: 'التخرج', en: 'Graduation' },
    description: { ar: 'بطاقات تهنئة بالتخرج', en: 'Graduation cards' },
    icon: '🎓',
    color: 'from-blue-400 to-cyan-600',
    priority: 7,
    category: 'personal',
    isPopular: true,
    isSeasonal: true
  },

  // National Events (Medium Priority)
  {
    id: 'saudi-national-day',
    name: { ar: 'اليوم الوطني السعودي', en: 'Saudi National Day' },
    description: { ar: 'بطاقات تهنئة باليوم الوطني', en: 'Saudi National Day cards' },
    icon: '🇸🇦',
    color: 'from-green-500 to-green-700',
    priority: 8,
    category: 'national',
    isPopular: true,
    isSeasonal: true
  },
  {
    id: 'saudi-foundation-day',
    name: { ar: 'يوم التأسيس السعودي', en: 'Saudi Foundation Day' },
    description: { ar: 'بطاقات تهنئة بيوم التأسيس', en: 'Saudi Foundation Day cards' },
    icon: '🏛️',
    color: 'from-blue-500 to-indigo-700',
    priority: 9,
    category: 'national',
    isPopular: true,
    isSeasonal: true
  },

  // Family Events (Medium Priority)
  {
    id: 'mothers-day',
    name: { ar: 'عيد الأم', en: 'Mother\'s Day' },
    description: { ar: 'بطاقات تهنئة بعيد الأم', en: 'Mother\'s Day cards' },
    icon: '🌹',
    color: 'from-pink-500 to-red-500',
    priority: 10,
    category: 'family',
    isPopular: true,
    isSeasonal: true
  },
  {
    id: 'fathers-day',
    name: { ar: 'عيد الأب', en: 'Father\'s Day' },
    description: { ar: 'بطاقات تهنئة بعيد الأب', en: 'Father\'s Day cards' },
    icon: '👔',
    color: 'from-blue-600 to-indigo-800',
    priority: 11,
    category: 'family',
    isPopular: true,
    isSeasonal: true
  },

  // General Celebrations (Lower Priority)
  {
    id: 'new-year',
    name: { ar: 'رأس السنة', en: 'New Year' },
    description: { ar: 'بطاقات تهنئة برأس السنة', en: 'New Year cards' },
    icon: '🎆',
    color: 'from-purple-500 to-pink-500',
    priority: 12,
    category: 'general',
    isPopular: false,
    isSeasonal: true
  }
];

// Arabic fonts configuration
const arabicFonts = [
  { name: "خط المهند", value: "Al-Mohanad", family: "'Al-Mohanad', serif" },
  { name: "خط القاهرة", value: "Cairo", family: "'Cairo', sans-serif" },
  { name: "خط أميري", value: "Amiri", family: "'Amiri', serif" },
  { name: "خط ريدكس برو", value: "RedexPro", family: "'RedexPro', sans-serif" },
  { name: "خط عارف رقعة", value: "ArefRuqaa", family: "'Aref Ruqaa', serif" },
  { name: "خط النظام", value: "Al-Nizam", family: "'Al-Nizam', serif" },
  { name: "خط الأصيل", value: "Al-Asil", family: "'Al-Asil', serif" },
  { name: "خط الأنيق", value: "Al-Aniq", family: "'Al-Aniq', serif" }
];

// Color options for text
const textColors = [
  { name: "أبيض", value: "#ffffff" },
  { name: "أسود", value: "#000000" },
  { name: "أحمر", value: "#ff0000" },
  { name: "أزرق", value: "#0000ff" },
  { name: "أخضر", value: "#00ff00" },
  { name: "أصفر", value: "#ffff00" },
  { name: "برتقالي", value: "#ffa500" },
  { name: "بنفسجي", value: "#800080" },
  { name: "ذهبي", value: "#ffd700" },
  { name: "فضي", value: "#c0c0c0" }
];

// ============================================================================
// TRANSLATIONS
// ============================================================================

const translations = {
  ar: {
    title: "إنشاء بطاقة معايدة",
    subtitle: "صمم بطاقة معايدة مخصصة باختيار البطاقة والخط المناسب",
    name: "الاسم",
    additionalText: "نص إضافي (اختياري)",
    selectFont: "اختر نوع الخط",
    fontSize: "حجم الخط",
    textColor: "لون النص",
    selectCard: "اختر البطاقة",
    selectCardType: "اختر نوع البطاقة",
    uploadImage: "أو قم برفع صورة خاصة بك",
    clickToUpload: "انقر للرفع أو اسحب الصورة هنا",
    fileTypes: "PNG, JPG أو JPEG (الحد الأقصى: 10MB)",
    downloadCard: "تنزيل البطاقة",
    backToHome: "العودة للصفحة الرئيسية",
    backToCardType: "العودة لاختيار نوع البطاقة",
    greetingCard: "بطاقة معايدة",
    preview: "معاينة البطاقة",
    customize: "تخصيص البطاقة"
  },
  en: {
    title: "Create Greeting Card",
    subtitle: "Design a custom greeting card by choosing the card and appropriate font",
    name: "Name",
    additionalText: "Additional Text (Optional)",
    selectFont: "Select Font Type",
    fontSize: "Font Size",
    textColor: "Text Color",
    selectCard: "Select Card",
    selectCardType: "Choose Card Type",
    uploadImage: "Or upload your own image",
    clickToUpload: "Click to upload or drag image here",
    fileTypes: "PNG, JPG or JPEG (Max: 10MB)",
    downloadCard: "Download Card",
    backToHome: "Back to Home",
    backToCardType: "Back to Card Type Selection",
    greetingCard: "Greeting Card",
    preview: "Card Preview",
    customize: "Customize Card"
  }
};

// ============================================================================
// CARD TEMPLATES & OCCASION TEMPLATES
// ============================================================================

// Default card templates - Easy to add new templates
const cardTemplates = [
  {
    id: 1,
    name: "Greeting Card 1",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 2,
    name: "Greeting Card 2", 
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 3,
    name: "Greeting Card 3",
    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 4,
    name: "Greeting Card 4",
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 5,
    name: "Greeting Card 5",
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 6,
    name: "Greeting Card 6",
    background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 7,
    name: "Greeting Card 7",
    background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 8,
    name: "Greeting Card 8",
    background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 9,
    name: "Greeting Card 9",
    background: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 10,
    name: "Greeting Card 10",
    background: "linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)",
    pattern: "none",
    image: null as string | null
  },
  {
    id: 11,
    name: "Greeting Card 11",
    background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    pattern: "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%)",
    image: null as string | null
  },
  {
    id: 12,
    name: "Greeting Card 12",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    pattern: "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
    image: null as string | null
  },
  {
    id: 13,
    name: "Greeting Card 13",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    pattern: "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)",
    image: null as string | null
  },
  {
    id: 14,
    name: "Greeting Card 14",
    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 8px)",
    image: null as string | null
  },
  {
    id: 15,
    name: "Greeting Card 15",
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    pattern: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2) 0%, transparent 70%)",
    image: null as string | null
  },
  {
    id: 16,
    name: "Greeting Card 16",
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    pattern: "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, transparent 0px), linear-gradient(0deg, rgba(255,255,255,0.1) 0px, transparent 0px)",
    image: null as string | null
  }
];

// Enhanced occasion templates with developer images - Easy to add new occasions
const occasionTemplates = {
  'eid-al-fitr': [
    {
      id: 1,
      name: "Eid al-Fitr 1",
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      pattern: "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/eid-al-fitr-1.jpg"
    },
    {
      id: 2,
      name: "Eid al-Fitr 2",
      background: "linear-gradient(135deg, #34d399 0%, #10b981 100%)",
      pattern: "none",
      image: "/images/cards/eid-al-fitr-2.jpg"
    },
    {
      id: 3,
      name: "Eid al-Fitr 3",
      background: "linear-gradient(135deg, #6ee7b7 0%, #34d399 100%)",
      pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/eid-al-fitr-3.jpg"
    }
  ],
  'eid-al-adha': [
    {
      id: 1,
      name: "Eid al-Adha 1",
      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      pattern: "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/eid-al-adha-1.jpg"
    },
    {
      id: 2,
      name: "Eid al-Adha 2",
      background: "linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
      pattern: "none",
      image: "/images/cards/eid-al-adha-2.jpg"
    },
    {
      id: 3,
      name: "Eid al-Adha 3",
      background: "linear-gradient(135deg, #fca5a5 0%, #f87171 100%)",
      pattern: "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)",
      image: "/images/cards/eid-al-adha-3.jpg"
    }
  ],
  'saudi-national-day': [
    {
      id: 1,
      name: "Saudi National Day 1",
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      pattern: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
      image: "/images/cards/saudi-national-1.jpg"
    },
    {
      id: 2,
      name: "Saudi National Day 2",
      background: "linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)",
      pattern: "repeating-linear-gradient(90deg, rgba(16, 185, 129, 0.1) 0px, rgba(16, 185, 129, 0.1) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/saudi-national-2.jpg"
    },
    {
      id: 3,
      name: "Saudi National Day 3",
      background: "linear-gradient(135deg, #10b981 0%, #047857 100%)",
      pattern: "none",
      image: "/images/cards/saudi-national-3.jpg"
    }
  ],
  'saudi-foundation-day': [
    {
      id: 1,
      name: "Saudi Foundation Day 1",
      background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      pattern: "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/saudi-foundation-1.jpg"
    },
    {
      id: 2,
      name: "Saudi Foundation Day 2",
      background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
      pattern: "none",
      image: "/images/cards/saudi-foundation-2.jpg"
    },
    {
      id: 3,
      name: "Saudi Foundation Day 3",
      background: "linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)",
      pattern: "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, transparent 0px), linear-gradient(0deg, rgba(255,255,255,0.1) 0px, transparent 0px)",
      image: "/images/cards/saudi-foundation-3.jpg"
    }
  ],
  'ramadan': [
    {
      id: 1,
      name: "Ramadan 1",
      background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      pattern: "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/ramadan-1.jpg"
    },
    {
      id: 2,
      name: "Ramadan 2",
      background: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
      pattern: "none",
      image: "/images/cards/ramadan-2.jpg"
    },
    {
      id: 3,
      name: "Ramadan 3",
      background: "linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)",
      pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/ramadan-3.jpg"
    },
    {
      id: 4,
      name: "Ramadan 4",
      background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
      pattern: "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)",
      image: "/images/cards/ramadan-4.jpg"
    },
    {
      id: 5,
      name: "Ramadan 5",
      background: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
      pattern: "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, transparent 0px), linear-gradient(0deg, rgba(255,255,255,0.1) 0px, transparent 0px)",
      image: "/images/cards/ramadan-5.jpg"
    },
    {
      id: 6,
      name: "Ramadan 6",
      background: "linear-gradient(135deg, #c4b5fd 0%, #8b5cf6 100%)",
      pattern: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
      image: "/images/cards/ramadan-6.jpg"
    },
    {
      id: 7,
      name: "Ramadan 7",
      background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
      pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/ramadan-7.jpg"
    },
    {
      id: 8,
      name: "Ramadan 8",
      background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
      pattern: "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)",
      image: "/images/cards/ramadan-8.jpg"
    },
    {
      id: 9,
      name: "Ramadan 9",
      background: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
      pattern: "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)",
      image: "/images/cards/ramadan-9.jpg"
    },
    {
      id: 10,
      name: "Ramadan 10",
      background: "linear-gradient(135deg, #c4b5fd 0%, #8b5cf6 100%)",
      pattern: "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/ramadan-10.jpg"
    }
  ],
  'graduate': [
    {
      id: 1,
      name: "Graduate 1",
      background: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      pattern: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
      image: "/images/cards/graduate-1.jpg"
    },
    {
      id: 2,
      name: "Graduate 2",
      background: "linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)",
      pattern: "none",
      image: "/images/cards/graduate-2.jpg"
    },
    {
      id: 3,
      name: "Graduate 3",
      background: "linear-gradient(135deg, #67e8f9 0%, #22d3ee 100%)",
      pattern: "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)",
      image: "/images/cards/graduate-3.jpg"
    }
  ],
  'new-born': [
    {
      id: 1,
      name: "New Born 1",
      background: "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
      pattern: "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.3) 0%, transparent 50%)",
      image: "/images/cards/new-born-1.jpg"
    },
    {
      id: 2,
      name: "New Born 2",
      background: "linear-gradient(135deg, #f9a8d4 0%, #f472b6 100%)",
      pattern: "none",
      image: "/images/cards/new-born-2.jpg"
    },
    {
      id: 3,
      name: "New Born 3",
      background: "linear-gradient(135deg, #fbcfe8 0%, #f9a8d4 100%)",
      pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/new-born-3.jpg"
    }
  ],
  'marriage': [
    {
      id: 1,
      name: "Marriage 1",
      background: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
      pattern: "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/marriage-1.jpg"
    },
    {
      id: 2,
      name: "Marriage 2",
      background: "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
      pattern: "none",
      image: "/images/cards/marriage-2.jpg"
    },
    {
      id: 3,
      name: "Marriage 3",
      background: "linear-gradient(135deg, #f9a8d4 0%, #f472b6 100%)",
      pattern: "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, transparent 0px), linear-gradient(0deg, rgba(255,255,255,0.1) 0px, transparent 0px)",
      image: "/images/cards/marriage-3.jpg"
    }
  ],
  'birthday': [
    {
      id: 1,
      name: "Birthday 1",
      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
      pattern: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
      image: "/images/cards/birthday-1.jpg"
    },
    {
      id: 2,
      name: "Birthday 2",
      background: "linear-gradient(135deg, #fcd34d 0%, #fbbf24 100%)",
      pattern: "none",
      image: "/images/cards/birthday-2.jpg"
    },
    {
      id: 3,
      name: "Birthday 3",
      background: "linear-gradient(135deg, #fde68a 0%, #fcd34d 100%)",
      pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/birthday-3.jpg"
    }
  ],
  'mothers-day': [
    {
      id: 1,
      name: "Mother's Day 1",
      background: "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
      pattern: "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/mothers-day-1.jpg"
    },
    {
      id: 2,
      name: "Mother's Day 2",
      background: "linear-gradient(135deg, #f9a8d4 0%, #f472b6 100%)",
      pattern: "none",
      image: "/images/cards/mothers-day-2.jpg"
    },
    {
      id: 3,
      name: "Mother's Day 3",
      background: "linear-gradient(135deg, #fbcfe8 0%, #f9a8d4 100%)",
      pattern: "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)",
      image: "/images/cards/mothers-day-3.jpg"
    }
  ],
  'fathers-day': [
    {
      id: 1,
      name: "Father's Day 1",
      background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      pattern: "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)",
      image: "/images/cards/fathers-day-1.jpg"
    },
    {
      id: 2,
      name: "Father's Day 2",
      background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
      pattern: "none",
      image: "/images/cards/fathers-day-2.jpg"
    },
    {
      id: 3,
      name: "Father's Day 3",
      background: "linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)",
      pattern: "repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 8px)",
      image: "/images/cards/fathers-day-3.jpg"
    }
  ],
  'new-year': [
    {
      id: 1,
      name: "New Year 1",
      background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      pattern: "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 70%)",
      image: "/images/cards/new-year-1.jpg"
    },
    {
      id: 2,
      name: "New Year 2",
      background: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
      pattern: "none",
      image: "/images/cards/new-year-2.jpg"
    },
    {
      id: 3,
      name: "New Year 3",
      background: "linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)",
      pattern: "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, transparent 0px), linear-gradient(0deg, rgba(255,255,255,0.1) 0px, transparent 0px)",
      image: "/images/cards/new-year-3.jpg"
    }
  ]
};

// ============================================================================
// IMAGE MANAGEMENT SYSTEM
// ============================================================================

// Image management system with better optimization and fallback handling
const imageManager = {
  // Available images cache to avoid repeated checks
  availableImages: new Set<string>(),
  
  // Initialize available images
  init: () => {
    // For now, we'll manually track available images
    // In a real app, you could fetch this from the server
    const available = [
      '/images/cards/ramadan-1.jpg',
      '/images/cards/ramadan-2.jpg', 
      '/images/cards/ramadan-3.jpg',
      '/images/cards/ramadan-4.jpg',
      '/images/cards/ramadan-5.jpg',
      '/images/cards/ramadan-6.jpg',
      '/images/cards/ramadan-7.jpg',
      '/images/cards/ramadan-8.jpg',
      '/images/cards/ramadan-9.jpg',
      '/images/cards/ramadan-10.jpg'
    ];
    available.forEach(img => imageManager.availableImages.add(img));
  },
  
  // Check if image exists and return fallback if not
  getImageUrl: (imagePath: string | null): string | null => {
    if (!imagePath) return null;
    
    // Check if image is available
    if (imageManager.availableImages.has(imagePath)) {
      return imagePath;
    }
    
    // Return null to use gradient fallback
    return null;
  },
  
  // Get available images for a card type
  getAvailableImages: (cardType: string): string[] => {
    const images = [];
    for (let i = 1; i <= 10; i++) { // Support up to 10 images per type
      const imagePath = `/images/cards/${cardType}-${i}.jpg`;
      if (imageManager.availableImages.has(imagePath)) {
        images.push(imagePath);
      }
    }
    return images;
  },
  
  // Check if image file exists
  imageExists: (imagePath: string): boolean => {
    return imageManager.availableImages.has(imagePath);
  },
  
  // Get optimized image URL with loading attributes
  getOptimizedImageUrl: (imagePath: string | null): string | null => {
    const url = imageManager.getImageUrl(imagePath);
    if (!url) return null;
    
    // Add loading optimization parameters
    return `${url}?optimize=true&quality=85`;
  }
};

// Initialize image manager
imageManager.init();

// ============================================================================
// HELPER FUNCTIONS & UTILITIES
// ============================================================================

// Utility function to create a new card template
// Usage: createCardTemplate(1, "New Card", "linear-gradient(...)", "pattern", "/image.jpg")
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createCardTemplate = (
  id: number, 
  name: string, 
  background: string, 
  pattern: string = "none", 
  image: string | null = null
) => ({
  id,
  name,
  background,
  pattern,
  image
});

// Utility function to create a new card type
// Usage: createCardType("christmas", {ar: "عيد الميلاد", en: "Christmas"}, {...}, "🎄", "from-red-400 to-green-600")
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createCardType = (
  id: string,
  name: { ar: string; en: string },
  description: { ar: string; en: string },
  icon: string,
  color: string
) => ({
  id,
  name,
  description,
  icon,
  color
});

// Utility function to create a new font option
// Usage: createFontOption("خط جديد", "NewFont", "'New Font', serif")
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createFontOption = (name: string, value: string, family: string) => ({
  name,
  value,
  family
});

// Utility function to create a new color option
// Usage: createColorOption("أزرق فاتح", "#87CEEB")
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createColorOption = (name: string, value: string) => ({
  name,
  value
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function GreetingCards() {
  const [lang, setLang] = useState<'ar' | 'en'>('ar');
  const [selectedCardType, setSelectedCardType] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [additionalText, setAdditionalText] = useState('');
  const [selectedFont, setSelectedFont] = useState(arabicFonts[0]);
  const [fontSize, setFontSize] = useState(26);
  const [textColor, setTextColor] = useState(textColors[0]);
  const [selectedCard, setSelectedCard] = useState(cardTemplates[0]);
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [namePosition, setNamePosition] = useState({ x: 0, y: 0 });
  const [additionalTextPosition, setAdditionalTextPosition] = useState({ x: 0, y: 0 });
  const [windowSize, setWindowSize] = useState({ width: 1024, height: 768 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const t = translations[lang];

  // Motion values for dragging
  const nameX = useMotionValue(0);
  const nameY = useMotionValue(0);
  const additionalTextX = useMotionValue(0);
  const additionalTextY = useMotionValue(0);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    // Set initial size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force re-render when window size changes to update card preview
  useEffect(() => {
    // This will trigger a re-render when windowSize changes
    // The card preview will update with new dimensions
  }, [windowSize.width, windowSize.height]);

  // Force re-render when selected card changes to ensure proper image sizing
  useEffect(() => {
    // This will trigger a re-render when selectedCard changes
    // The card preview will update with new image and dimensions
  }, [selectedCard, customImage]);

  // Get current card templates based on selected type
  const getCurrentCardTemplates = () => {
    if (selectedCardType && occasionTemplates[selectedCardType as keyof typeof occasionTemplates]) {
      return occasionTemplates[selectedCardType as keyof typeof occasionTemplates];
    }
    return cardTemplates;
  };

  // Get proper background style for preview
  const getPreviewBackground = () => {
    if (customImage) {
      return `url(${customImage})`;
    }
    
    const imageUrl = imageManager.getOptimizedImageUrl(selectedCard.image);
    if (imageUrl) {
      return `url(${imageUrl})`;
    }
    
    return selectedCard.background;
  };

  // Enhanced image handling with better optimization and fallbacks
  const getCardBackgroundStyle = (card: typeof cardTemplates[0]) => {
    if (customImage) {
      return {
        background: `url(${customImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        imageRendering: 'crisp-edges' as const,
        objectFit: 'cover' as const,
        willChange: 'transform' as const
      };
    }
    
    const imageUrl = imageManager.getOptimizedImageUrl(card.image);
    if (imageUrl) {
      return {
        background: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        imageRendering: 'crisp-edges' as const,
        objectFit: 'cover' as const,
        willChange: 'transform' as const
      };
    }
    
    // Enhanced gradient fallback with pattern
    const backgroundStyle: React.CSSProperties = {
      background: card.background,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };
    
    // Add pattern overlay if specified
    if (card.pattern && card.pattern !== "none") {
      backgroundStyle.background = `${card.pattern}, ${card.background}`;
    }
    
    return backgroundStyle;
  };

  // Get preview dimensions based on current window size - Fixed sizing to fit container
  const getPreviewDimensions = () => {
    // Responsive scaling based on screen size with container constraints
    const isMobile = windowSize.width < 768;
    const isTablet = windowSize.width >= 768 && windowSize.width < 1024;
    const isLargeScreen = windowSize.width >= 1440;
    
    // Calculate available space in the preview container
    // Account for padding, borders, and minimum container height
    const containerPadding = 64; // p-8 = 32px * 2
    const containerMinHeight = 700; // Increased from 600 to 700 for larger preview
    const availableWidth = isMobile ? windowSize.width - 40 : 600; // Increased from 500 to 600
    const availableHeight = Math.max(containerMinHeight - containerPadding, 500); // Increased from 400 to 500
    
    let maxWidth, maxHeight;
    
    if (isMobile) {
      maxWidth = Math.min(320, availableWidth - 40); // Increased from 280 to 320
      maxHeight = Math.min(480, availableHeight - 40); // Increased from 420 to 480
    } else if (isTablet) {
      maxWidth = Math.min(380, availableWidth - 60); // Increased from 320 to 380
      maxHeight = Math.min(570, availableHeight - 60); // Increased from 480 to 570
    } else if (isLargeScreen) {
      maxWidth = Math.min(480, availableWidth - 80); // Increased from 400 to 480
      maxHeight = Math.min(720, availableHeight - 80); // Increased from 600 to 720
    } else {
      maxWidth = Math.min(420, availableWidth - 70); // Increased from 360 to 420
      maxHeight = Math.min(630, availableHeight - 70); // Increased from 540 to 630
    }
    
    // Calculate scale to fit within max dimensions while maintaining 9:16 ratio
    const scaleX = maxWidth / CARD_WIDTH;
    const scaleY = maxHeight / CARD_HEIGHT;
    const scale = Math.min(scaleX, scaleY);
    
    // Ensure reasonable scale range with higher minimum for better visibility
    const minScale = 0.3; // Increased from 0.25 to 0.3 for better visibility
    const maxScale = 0.8; // Increased from 0.75 to 0.8 for larger preview
    const finalScale = Math.max(minScale, Math.min(scale, maxScale));
    
    const dimensions = {
      width: Math.round(CARD_WIDTH * finalScale),
      height: Math.round(CARD_HEIGHT * finalScale),
      scale: finalScale
    };
    
    return dimensions;
  };

  // New function to get proper image fitting styles for preview
  // Removed - now handling image styling directly in preview container

  // Handle card type selection with image reset
  const handleCardTypeSelect = (cardTypeId: string) => {
    setSelectedCardType(cardTypeId);
    const templates = occasionTemplates[cardTypeId as keyof typeof occasionTemplates] || cardTemplates;
    setSelectedCard(templates[0]);
    // Reset custom image when changing card type
    setCustomImage(null);
  };

  // Handle card template selection with image reset and loading state
  const handleCardSelect = (card: typeof cardTemplates[0]) => {
    setSelectedCard(card);
    // Reset custom image when changing card template
    setCustomImage(null);
    
    // Set loading state if card has an image
    if (card.image && imageManager.imageExists(card.image)) {
      setIsImageLoading(true);
      // Simulate image loading time for better UX
      setTimeout(() => {
        setIsImageLoading(false);
      }, 300);
    } else {
      setIsImageLoading(false);
    }
  };

  // Handle file upload with improved quality and processing
  const handleFileUpload = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert(lang === 'ar' ? 'حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت' : 'File size too large. Maximum 5MB allowed');
        return;
      }

      // Check file type more strictly
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert(lang === 'ar' ? 'يرجى اختيار ملف صورة بصيغة JPG أو PNG أو WebP' : 'Please select an image file in JPG, PNG, or WebP format');
        return;
      }

      setIsUploading(true);
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for image processing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            setIsUploading(false);
            alert(lang === 'ar' ? 'خطأ في معالجة الصورة' : 'Error processing image');
            return;
          }

          // Force 9:16 aspect ratio (1080x1920)
          const targetWidth = 1080;
          const targetHeight = 1920;
          
          // Set canvas dimensions to target size
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          // Apply image processing for better quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Calculate scaling to maintain aspect ratio and fill the canvas
          const scaleX = targetWidth / img.width;
          const scaleY = targetHeight / img.height;
          const scale = Math.max(scaleX, scaleY); // Use max to ensure full coverage
          
          // Calculate dimensions to center the image
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          const x = (targetWidth - scaledWidth) / 2;
          const y = (targetHeight - scaledHeight) / 2;
          
          // Draw image with proper scaling and centering
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

          // Convert to optimized data URL with quality control
          const quality = file.size > 2 * 1024 * 1024 ? 0.85 : 0.95; // Lower quality for larger files
          const processedImageUrl = canvas.toDataURL('image/jpeg', quality);
          
          setCustomImage(processedImageUrl);
          setIsUploading(false);
          
          // Show success message
          console.log('Image uploaded successfully:', {
            originalSize: file.size,
            originalDimensions: `${img.width}x${img.height}`,
            processedDimensions: `${targetWidth}x${targetHeight}`,
            quality: quality
          });
        };
        
        img.onerror = () => {
          setIsUploading(false);
          alert(lang === 'ar' ? 'خطأ في تحميل الصورة. يرجى التأكد من أن الملف صورة صالحة' : 'Error loading image. Please ensure the file is a valid image');
        };
        
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => {
        setIsUploading(false);
        alert(lang === 'ar' ? 'خطأ في قراءة الملف. يرجى المحاولة مرة أخرى' : 'Error reading file. Please try again');
      };
      
      reader.readAsDataURL(file);
    } else {
      alert(lang === 'ar' ? 'يرجى اختيار ملف صورة صالح (JPG, PNG, أو WebP)' : 'Please select a valid image file (JPG, PNG, or WebP)');
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle text dragging with proper scaling - allow movement across entire image
  const handleNameDrag = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // تحويل الحركة من مقياس المعاينة إلى الحجم الكامل
    const previewScale = getPreviewDimensions().scale;
    const newX = namePosition.x + (info.delta.x / previewScale);
    const newY = namePosition.y + (info.delta.y / previewScale);
    
    // Allow movement across the entire card space (with some padding for text visibility)
    const maxX = CARD_WIDTH * 0.6; // Increased from 0.45 to 0.6 for more movement
    const maxY = CARD_HEIGHT * 0.6; // Increased from 0.45 to 0.6 for more movement
    
    setNamePosition({
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    });
  };

  const handleAdditionalTextDrag = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // تحويل الحركة من مقياس المعاينة إلى الحجم الكامل
    const previewScale = getPreviewDimensions().scale;
    const newX = additionalTextPosition.x + (info.delta.x / previewScale);
    const newY = additionalTextPosition.y + (info.delta.y / previewScale);
    
    // Allow movement across the entire card space (with some padding for text visibility)
    const maxX = CARD_WIDTH * 0.6; // Increased from 0.45 to 0.6 for more movement
    const maxY = CARD_HEIGHT * 0.6; // Increased from 0.45 to 0.6 for more movement
    
    setAdditionalTextPosition({
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    });
  };

  // Get text styles for preview (scaled)
  const getPreviewTextStyles = (isName: boolean = true) => {
    const previewScale = getPreviewDimensions().scale;
    const baseFontSize = isName ? fontSize : Math.max(fontSize * 0.6, 16);
    const scaledFontSize = Math.round(baseFontSize * previewScale);
    const position = isName ? namePosition : additionalTextPosition;
    
    return {
      fontFamily: selectedFont.family,
      fontSize: `${scaledFontSize}px`,
      color: textColor.value,
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
      fontWeight: isName ? 'bold' : 'normal',
      lineHeight: isName ? '1.2' : '1.4',
      textAlign: 'center' as const,
      maxWidth: isName ? undefined : `${getPreviewDimensions().width * 0.8}px`,
      whiteSpace: 'pre-wrap' as const,
      wordBreak: 'break-word' as const,
      position: 'absolute' as const,
      transform: `translate(${position.x * previewScale}px, ${position.y * previewScale}px)`,
      zIndex: isDraggingText ? 10 : 1,
      pointerEvents: 'auto' as const
    };
  };

  // Reset text positions and custom image
  const resetTextPositions = () => {
    setNamePosition({ x: 0, y: 0 });
    setAdditionalTextPosition({ x: 0, y: 0 });
    nameX.set(0);
    nameY.set(0);
    additionalTextX.set(0);
    additionalTextY.set(0);
  };

  // Clear custom image
  const clearCustomImage = () => {
    setCustomImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Download card as image using Canvas API directly
  const downloadCard = async () => {
    try {
      console.log('Starting downloadCard with Canvas API');
      
      // إنشاء canvas بالحجم الكامل
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Could not get canvas context');
        alert(lang === 'ar' ? 'خطأ في إنشاء الصورة' : 'Error creating image');
        return;
      }
      
      // تعيين أبعاد canvas
      canvas.width = CARD_WIDTH;
      canvas.height = CARD_HEIGHT;
      
      console.log(`Canvas created with dimensions: ${CARD_WIDTH}x${CARD_HEIGHT}`);
      
      // 1. رسم الخلفية
      if (customImage) {
        // إذا كانت هناك صورة مخصصة
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = customImage;
        });
        
        ctx.drawImage(img, 0, 0, CARD_WIDTH, CARD_HEIGHT);
        console.log('Custom image drawn');
      } else if (selectedCard.image && imageManager.imageExists(selectedCard.image)) {
        // إذا كانت هناك صورة للبطاقة
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageManager.getOptimizedImageUrl(selectedCard.image) || '';
        });
        
        ctx.drawImage(img, 0, 0, CARD_WIDTH, CARD_HEIGHT);
        console.log('Card image drawn');
      } else {
        // رسم تدرج لوني كخلفية
        const gradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
        gradient.addColorStop(0, '#667eea');
        gradient.addColorStop(1, '#764ba2');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
        console.log('Gradient background drawn');
      }
      
      // 2. رسم النصوص
      if (name || additionalText) {
        // تحميل الخط إذا كان مخصصًا
        if (selectedFont.value !== 'Arial') {
          try {
            await document.fonts.load(`${fontSize}px ${selectedFont.family}`);
            console.log('Custom font loaded');
          } catch (error) {
            console.warn('Could not load custom font, using fallback');
          }
        }
        
        // إعداد النص
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // رسم الاسم
        if (name) {
          ctx.font = `bold ${fontSize}px ${selectedFont.family}, Arial, sans-serif`;
          ctx.fillStyle = textColor.value;
          ctx.shadowColor = 'rgba(0,0,0,0.3)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
          
          // استخدام المواقع مباشرة كما هي محفوظة
          let nameX = CARD_WIDTH / 2 + namePosition.x;
          let nameY = CARD_HEIGHT / 2 + namePosition.y;
          
          // التأكد من أن النص داخل حدود البطاقة
          nameX = Math.max(100, Math.min(CARD_WIDTH - 100, nameX));
          nameY = Math.max(100, Math.min(CARD_HEIGHT - 100, nameY));
          
          // تقسيم النص إلى أسطر إذا كان طويلاً
          const maxWidth = CARD_WIDTH * 0.8;
          const words = name.split(' ');
          const lines = [];
          let currentLine = words[0];
          
          for (let i = 1; i < words.length; i++) {
            const testLine = currentLine + ' ' + words[i];
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth) {
              lines.push(currentLine);
              currentLine = words[i];
            } else {
              currentLine = testLine;
            }
          }
          lines.push(currentLine);
          
          // رسم كل سطر
          const lineHeight = fontSize * 1.2;
          const totalHeight = lines.length * lineHeight;
          const startY = nameY - (totalHeight / 2) + (lineHeight / 2);
          
          lines.forEach((line, index) => {
            ctx.fillText(line, nameX, startY + (index * lineHeight));
          });
          
          console.log('Name text drawn at:', nameX, nameY, 'original position:', namePosition);
        }
        
        // رسم النص الإضافي
        if (additionalText) {
          ctx.font = `${Math.max(fontSize * 0.6, 16)}px ${selectedFont.family}, Arial, sans-serif`;
          ctx.fillStyle = textColor.value;
          ctx.shadowColor = 'rgba(0,0,0,0.3)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
          
          // استخدام المواقع مباشرة كما هي محفوظة
          let textX = CARD_WIDTH / 2 + additionalTextPosition.x;
          let textY = CARD_HEIGHT / 2 + additionalTextPosition.y + 100;
          
          // التأكد من أن النص داخل حدود البطاقة
          textX = Math.max(100, Math.min(CARD_WIDTH - 100, textX));
          textY = Math.max(100, Math.min(CARD_HEIGHT - 100, textY));
          
          // تقسيم النص إلى أسطر
          const maxWidth = CARD_WIDTH * 0.8;
          const words = additionalText.split(' ');
          const lines = [];
          let currentLine = words[0];
          
          for (let i = 1; i < words.length; i++) {
            const testLine = currentLine + ' ' + words[i];
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth) {
              lines.push(currentLine);
              currentLine = words[i];
            } else {
              currentLine = testLine;
            }
          }
          lines.push(currentLine);
          
          // رسم كل سطر
          const lineHeight = Math.max(fontSize * 0.6, 16) * 1.4;
          const totalHeight = lines.length * lineHeight;
          const startY = textY - (totalHeight / 2) + (lineHeight / 2);
          
          lines.forEach((line, index) => {
            ctx.fillText(line, textX, startY + (index * lineHeight));
          });
          
          console.log('Additional text drawn at:', textX, textY, 'original position:', additionalTextPosition);
        }
      }
      
      console.log('Canvas drawing completed');
      
      // 3. تحويل canvas إلى صورة وتنزيلها
      const dataURL = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `${selectedCardType || 'greeting'}-card-${Date.now()}.png`;
      link.href = dataURL;
      link.click();
      
      console.log('Download completed');
      
    } catch (error) {
      console.error('Error downloading card:', error);
      alert(lang === 'ar' ? 'خطأ في تنزيل البطاقة. يرجى المحاولة مرة أخرى.' : 'Error downloading card. Please try again.');
    }
  };

  // Toggle language
  const toggleLanguage = () => {
    setLang(prev => prev === 'ar' ? 'en' : 'ar');
  };

  return (
    <div className="greeting-cards-app" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header with Glass Effect - مطابق لحاسبة العمر */}
      <div className="greeting-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Dynamic Back Button */}
            {selectedCardType ? (
              <button
                onClick={() => setSelectedCardType(null)}
                className="greeting-back-button-enhanced"
                aria-label={lang === 'ar' ? "العودة لاختيار نوع البطاقة" : "Back to Card Type Selection"}
              >
                {lang === 'ar' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19l7-7-7-7" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {lang === 'ar' ? 'اختيار نوع البطاقة' : 'Select Card Type'}
                </span>
              </button>
            ) : (
              <Link
                to="/"
                className="greeting-back-button-enhanced"
                aria-label={lang === 'ar' ? "العودة إلى الصفحة الرئيسية" : "Back to Main Page"}
              >
                {lang === 'ar' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {lang === 'ar' ? 'الصفحة الرئيسية' : 'Home'}
                </span>
              </Link>
            )}
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleLanguage}
                className="lang-switcher"
                aria-label={lang === 'ar' ? "Switch to English" : "التبديل إلى العربية"}
              >
                {lang === 'ar' ? 'EN' : 'عربي'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Advanced Effects - Enhanced Visual Identity */}
      <section className="greeting-hero-section">
        {/* Hero Background Effects */}
        <div className="greeting-hero-background"></div>
        
        {/* Hero Decorative Elements */}
        <div className="greeting-hero-decoration"></div>
        
        {/* Hero Content */}
        <div className="greeting-hero-content">
          <div className="greeting-hero-icon-container">
            <span className="greeting-hero-icon" role="img" aria-label="Greeting Card Icon">🎨</span>
          </div>
          {/* Main Title with Advanced Styling */}
          <h1 className="greeting-hero-title">
            {t.title}
          </h1>
          
          {/* Subtitle with Enhanced Styling */}
          <p className="greeting-hero-subtitle">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="greeting-main-content container mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[70vh]">
        {/* Card Type Selection */}
        {!selectedCardType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
              {t.selectCardType}
            </h2>
            <div className="greeting-sections-container">
              <div className="greeting-sections-grid">
                {cardTypes.map((cardType) => (
                  <motion.button
                    key={cardType.id}
                    onClick={() => handleCardTypeSelect(cardType.id)}
                    className="greeting-section-card-enhanced"
                    data-priority={cardType.priority}
                    data-category={cardType.category}
                    data-popular={cardType.isPopular}
                    data-seasonal={cardType.isSeasonal}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.3, 
                      delay: cardType.priority <= 3 ? cardType.priority * 0.05 : 0.15 + (cardType.priority - 3) * 0.02
                    }}
                  >
                    <div className="greeting-card-header">
                      <div className={`greeting-card-icon-container bg-gradient-to-br ${cardType.color}`}>
                        {cardType.icon}
                      </div>
                      
                      <div className="greeting-card-content">
                        <h3 className="greeting-card-title">
                          {cardType.name[lang]}
                        </h3>
                        <p className="greeting-card-description">
                          {cardType.description[lang]}
                        </p>
                        
                        {/* Category Label */}
                        <div className="greeting-card-category-label">
                          {lang === 'ar' 
                            ? cardType.category === 'religious' ? 'ديني' 
                              : cardType.category === 'personal' ? 'شخصي'
                              : cardType.category === 'national' ? 'وطني'
                              : cardType.category === 'family' ? 'عائلي'
                              : 'عام'
                            : cardType.category === 'religious' ? 'Religious' 
                              : cardType.category === 'personal' ? 'Personal'
                              : cardType.category === 'national' ? 'National'
                              : cardType.category === 'family' ? 'Family'
                              : 'General'
                          }
                        </div>
                      </div>
                      
                      {/* Priority Badge for top cards */}
                      {cardType.priority <= 3 && (
                        <div className="greeting-priority-badge">
                          {lang === 'ar' ? 'الأكثر استخداماً' : 'Most Popular'}
                        </div>
                      )}
                      
                      {/* Section Indicators */}
                      <div className="greeting-section-indicator">
                        {Array.from({ length: 3 }, (_, i) => (
                          <div key={i} className="indicator-dot"></div>
                        ))}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Content - Only show when card type is selected */}
        {selectedCardType && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Back to card type selection */}
            <div className="lg:col-span-2 mb-4">
              <button
                onClick={() => setSelectedCardType(null)}
                className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors font-medium"
              >
                {lang === 'ar' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19l7-7-7-7" />
                  </svg>
                )}
                <span className="text-sm">
                  {t.backToCardType}
                </span>
              </button>
            </div>

            {/* Customization Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6"
            >
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
                {t.customize}
              </h2>

              {/* Selected Card Type Display */}
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${cardTypes.find(c => c.id === selectedCardType)?.color} flex items-center justify-center text-xl`}>
                    {cardTypes.find(c => c.id === selectedCardType)?.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 dark:text-white">
                      {cardTypes.find(c => c.id === selectedCardType)?.name[lang]}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {cardTypes.find(c => c.id === selectedCardType)?.description[lang]}
                    </p>
                  </div>
                </div>
              </div>

              {/* Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.name}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={lang === 'ar' ? 'أدخل اسمك هنا' : 'Enter your name here'}
                />
              </div>

              {/* Additional Text */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.additionalText}
                </label>
                <textarea
                  value={additionalText}
                  onChange={(e) => setAdditionalText(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  placeholder={lang === 'ar' ? 'أضف نص إضافي هنا...' : 'Add additional text here...'}
                />
              </div>

              {/* Font Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.selectFont}
                </label>
                <select
                  value={selectedFont.value}
                  onChange={(e) => {
                    const font = arabicFonts.find(f => f.value === e.target.value);
                    if (font) setSelectedFont(font);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {arabicFonts.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Font Size */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.fontSize}
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="16"
                    max="72"
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[60px]">
                    {fontSize}px
                  </span>
                </div>
              </div>

              {/* Text Color */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.textColor}
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {textColors.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setTextColor(color)}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        textColor.value === color.value
                          ? 'border-purple-500 scale-110'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Card Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.selectCard}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-64 overflow-y-auto p-1">
                  {getCurrentCardTemplates().map((card) => (
                    <button
                      key={card.id}
                      onClick={() => handleCardSelect(card)}
                      className={`aspect-[9/16] rounded-lg border-2 transition-all relative overflow-hidden ${
                        selectedCard.id === card.id
                          ? 'border-purple-500 scale-105 ring-2 ring-purple-200 dark:ring-purple-800'
                          : 'border-gray-300 dark:border-gray-600 hover:scale-105 hover:border-purple-400'
                      }`}
                      style={getCardBackgroundStyle(card)}
                      title={card.name}
                    >
                      {/* Loading indicator for card thumbnails */}
                      {card.image && imageManager.imageExists(card.image) && isImageLoading && selectedCard.id === card.id && (
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                          <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                      
                      {/* Card name overlay for better identification */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center truncate">
                        {card.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Image Upload - Enhanced */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.uploadImage}
                </label>
                <div className="space-y-3">
                  <button
                    type="button"
                    disabled={isUploading}
                    className={`w-full border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                      isDragging
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 scale-105'
                        : isUploading
                        ? 'border-gray-400 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    tabIndex={0}
                    aria-label={t.clickToUpload}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                        // Reset input value to allow selecting the same file again
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                    {isUploading ? (
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <svg className="animate-spin w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {lang === 'ar' ? 'جاري معالجة الصورة...' : 'Processing image...'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {lang === 'ar' ? 'يرجى الانتظار' : 'Please wait'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                          {t.clickToUpload}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {lang === 'ar' ? 'JPG, PNG, WebP (الحد الأقصى: 5 ميجابايت)' : 'JPG, PNG, WebP (Max: 5MB)'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {lang === 'ar' ? 'أو اسحب الصورة هنا' : 'or drag image here'}
                        </p>
                      </>
                    )}
                  </button>
                  
                  {/* Enhanced uploaded image preview */}
                  {customImage && (
                    <div className="relative bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {lang === 'ar' ? 'الصورة المرفوعة' : 'Uploaded Image'}
                        </span>
                        <button
                          onClick={clearCustomImage}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title={lang === 'ar' ? 'إزالة الصورة' : 'Remove image'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <img 
                        src={customImage} 
                        alt="Custom background" 
                        className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                      />
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                        {lang === 'ar' ? 'تم تحميل الصورة بنجاح' : 'Image uploaded successfully'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reset Positions Button */}
              <div className="mb-6">
                <button
                  onClick={resetTextPositions}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{lang === 'ar' ? 'إعادة تعيين مواقع النصوص' : 'Reset Text Positions'}</span>
                </button>
              </div>

              {/* Download Button */}
              <button
                onClick={downloadCard}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{t.downloadCard}</span>
              </button>
            </motion.div>

            {/* Card Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col items-center"
            >
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
                {t.preview}
              </h2>
              
              <div className="relative">
                {/* Preview Container with enhanced styling and better spacing */}
                <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700 min-h-[600px] flex items-center justify-center overflow-hidden">
                  <div
                    key={`${selectedCard.id}-${customImage ? 'custom' : 'default'}`}
                    ref={cardRef}
                    className="rounded-2xl shadow-xl overflow-hidden relative border-4 border-white dark:border-gray-700 flex-shrink-0 bg-cover bg-center"
                    style={{
                      width: `${getPreviewDimensions().width}px`,
                      height: `${getPreviewDimensions().height}px`,
                      boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      display: 'block',
                      background: getPreviewBackground(),
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      imageRendering: 'crisp-edges',
                      willChange: 'transform',
                      minWidth: '240px',
                      minHeight: '360px',
                      aspectRatio: '9/16'
                    }}
                  >
                    {/* Loading overlay */}
                    {isImageLoading && (
                      <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center z-10">
                        <div className="flex items-center space-x-2">
                          <svg className="animate-spin w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {lang === 'ar' ? 'جاري تحميل الصورة...' : 'Loading image...'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Pattern overlay */}
                    {selectedCard.pattern !== "none" && (
                      <div
                        className="absolute inset-0"
                        style={{ background: selectedCard.pattern }}
                      />
                    )}
                    
                    {/* Content overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                      {name && (
                        <motion.h1
                          drag
                          dragMomentum={false}
                          dragElastic={0.1}
                          dragConstraints={{
                            left: -getPreviewDimensions().width * 0.6,
                            right: getPreviewDimensions().width * 0.6,
                            top: -getPreviewDimensions().height * 0.6,
                            bottom: getPreviewDimensions().height * 0.6
                          }}
                          onDrag={handleNameDrag}
                          onDragStart={() => setIsDraggingText(true)}
                          onDragEnd={() => setIsDraggingText(false)}
                          className="font-bold mb-4 leading-tight cursor-move select-none"
                          style={getPreviewTextStyles()}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {name}
                        </motion.h1>
                      )}
                      
                      {additionalText && (
                        <motion.p
                          drag
                          dragMomentum={false}
                          dragElastic={0.1}
                          dragConstraints={{
                            left: -getPreviewDimensions().width * 0.6,
                            right: getPreviewDimensions().width * 0.6,
                            top: -getPreviewDimensions().height * 0.6,
                            bottom: getPreviewDimensions().height * 0.6
                          }}
                          onDrag={handleAdditionalTextDrag}
                          onDragStart={() => setIsDraggingText(true)}
                          onDragEnd={() => setIsDraggingText(false)}
                          className="leading-relaxed cursor-move select-none"
                          style={getPreviewTextStyles(false)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {additionalText}
                        </motion.p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Enhanced size information and controls */}
                <div className="mt-6 text-center space-y-3">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {lang === 'ar' ? 'اسحب النصوص لتغيير مواقعها' : 'Drag texts to reposition them'}
                    </p>
                    <div className="flex items-center justify-center space-x-4 text-xs text-gray-500 dark:text-gray-500">
                      <span className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span>{CARD_WIDTH}×{CARD_HEIGHT}px</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                        </svg>
                        <span>9:16</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span>{getPreviewDimensions().width}×{getPreviewDimensions().height}px</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Quick action buttons */}
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={resetTextPositions}
                      className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center space-x-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>{lang === 'ar' ? 'إعادة تعيين' : 'Reset'}</span>
                    </button>
                    
                    {customImage && (
                      <button
                        onClick={clearCustomImage}
                        className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors flex items-center space-x-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>{lang === 'ar' ? 'إزالة الصورة' : 'Remove Image'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </main>
    </div>
  );
} 